import type { DraftMachine, DraftParameter } from "@opus-dt/shared-types";
import type {
  MachineDetail as EditMachineDetail,
  ParameterDetail,
} from "@/types/line-editor.types";
import type { MachineDetail as ListMachineDetail } from "@/types/lines.types";
import type { NewParameterInput } from "../bridge-types";

export function draftMachineToDetail(m: DraftMachine): ListMachineDetail {
  return {
    id: m.tmpId,
    // Spec 080 — o modelo 3D volta do rascunho: sem ele, reabrir a máquina mostrava "Nenhum"
    // logo depois de o gestor ter escolhido um.
    model3dId: m.basic.model3dId ?? null,
    code: m.basic.code,
    name: m.basic.name,
    type: m.basic.type,
    status: "NODATA",
    statusSince: null,
    influencesStatus: false,
    requiresOperator: m.basic.requiresOperator ?? false,
    stoppedCostPerHour: m.basic.stoppedCostPerHour ?? null,
  };
}

export function draftMachineToEditDetail(m: DraftMachine): EditMachineDetail {
  return {
    id: m.tmpId,
    lineId: "draft:new",
    code: m.basic.code,
    name: m.basic.name,
    type: m.basic.type,
    status: "NODATA",
    positionX: 0,
    positionY: 0,
    requiresOperator: m.basic.requiresOperator ?? false,
    stoppedCostPerHour:
      m.basic.stoppedCostPerHour != null ? String(m.basic.stoppedCostPerHour) : null,
    deletedAt: null,
  };
}

export function draftParamToDetail(p: DraftParameter, machineTmpId: string): ParameterDetail {
  return {
    id: p.tmpId,
    // Spec 080 — papel visual e limites voltam do rascunho. Eram os outros dois campos que a barra
    // lateral deixava preencher e o salvamento engolia sem aviso.
    visualRole: "visualRole" in p ? (p.visualRole ?? "NONE") : "NONE",
    limitMax: "limitMax" in p ? (p.limitMax ?? null) : null,
    limitMin: "limitMin" in p ? (p.limitMin ?? null) : null,
    machineId: machineTmpId,
    name: p.name,
    description: p.description ?? null,
    category: p.category,
    unit: "unit" in p ? p.unit : null,
    dataType: deriveDataType(p.category),
    source: p.source,
    influencesSimulation: false,
    influencesStatus: p.category === "STATUS",
    productionRole: "productionRole" in p ? (p.productionRole ?? null) : null,
    isRestriction: "isRestriction" in p ? (p.isRestriction ?? false) : false,
    // Flag persistido no draft (STATUS); default on para os demais.
    generatesAlarm: "generatesAlarm" in p ? (p.generatesAlarm ?? true) : true,
    currentValue: null,
    statusMapping: "statusMapping" in p ? p.statusMapping : null,
  };
}

function deriveDataType(category: DraftParameter["category"]): string {
  switch (category) {
    case "NUMERIC":
      return "number";
    case "COUNTER":
      return "integer";
    case "BOOLEAN":
      return "boolean";
    case "TEXT":
    case "STATUS":
      return "string";
    case "DURATION":
      return "duration";
  }
}

export function newParameterToDraft(input: NewParameterInput): Omit<DraftParameter, "tmpId"> {
  const base = {
    name: input.name,
    description: input.description,
    source: input.source ?? "MANUAL",
    // Spec 080 — o papel viaja em toda categoria; quem julga se ele cabe é o service, na gravação
    // (`assertVisualRoleCompatible`). Duplicar a regra aqui criaria dois lugares para mudar juntos.
    visualRole: input.visualRole ?? "NONE",
  };
  switch (input.category) {
    case "STATUS":
      return {
        ...base,
        category: "STATUS",
        statusMapping: input.statusMapping ?? {},
        generatesAlarm: input.generatesAlarm ?? true,
      } as Omit<DraftParameter, "tmpId">;
    case "NUMERIC":
      return {
        ...base,
        category: "NUMERIC",
        unit: input.unit ?? "",
        isRestriction: input.isRestriction,
        limitMax: input.limitMax ?? null,
        limitMin: input.limitMin ?? null,
      } as Omit<DraftParameter, "tmpId">;
    case "COUNTER":
      return {
        ...base,
        category: "COUNTER",
        unit: input.unit ?? "",
        // Spec 081 — o rascunho carrega o contador INTEIRO. Forward só do sentido descartava
        // produto, tipo de perda e equivalência antes mesmo de chegar ao envio.
        productionRole: input.productionRole ?? undefined,
        productId: input.productId ?? undefined,
        lossType: input.lossType ?? undefined,
        unitFactor: input.unitFactor ?? undefined,
      } as Omit<DraftParameter, "tmpId">;
    case "BOOLEAN":
      return { ...base, category: "BOOLEAN" };
    case "TEXT":
      return { ...base, category: "TEXT" };
    case "DURATION":
      return {
        ...base,
        category: "DURATION",
        unit: (input.unit as "s" | "min" | "h") ?? "min",
      } as Omit<DraftParameter, "tmpId">;
  }
}
